import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import config from '../config.js';

/** How long to wait before asking again about a clip that is still being made. */
export const CLIP_POLL_MS = 30_000;

/**
 * The replay clip of one memory (ENG-758), from GET /me/memories/{id}/clip
 * (ENG-757). `state` is one of:
 *
 * - `idle`: nothing asked yet (call `load`);
 * - `loading`: the request is in flight;
 * - `ready`: `clip` holds `{ url, thumbnailUrl, expiresAt }`;
 * - `processing`: the clip is still being made; asked again every
 *   CLIP_POLL_MS while mounted;
 * - `unavailable`: no recording holds the song, or it isn't the caller's;
 * - `premium`: the listener needs Premium (402 premium_required);
 * - `error`: anything else (`load` tries again).
 *
 * The presigned URLs last 15 minutes. `fresh()` answers a clip whose URL is
 * still good, asking again first when `expiresAt` has passed, so a player
 * left open never plays a dead link; `refresh()` asks again unconditionally
 * (the player calls it when the video fails to load).
 */
export default function useMemoryClip(memoryId, { pollMs = CLIP_POLL_MS, now = () => Date.now() } = {}) {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const [state, setState] = useState('idle');
  const [clip, setClip] = useState(null);
  const clipRef = useRef(null);
  const nowRef = useRef(now);
  nowRef.current = now;
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    if (memoryId == null) return null;
    const request = ++requestRef.current;
    setState((prev) => (prev === 'ready' ? prev : 'loading'));
    let next = null;
    let nextState;
    try {
      const body = await fetchWrapperRef.current.get(`${config.apiBaseUrl}/me/memories/${memoryId}/clip`);
      if (body?.status === 'ready' && body.url) {
        next = { url: body.url, thumbnailUrl: body.thumbnailUrl || null, expiresAt: body.expiresAt || null };
        nextState = 'ready';
      } else {
        nextState = 'processing';
      }
    } catch (err) {
      if (err?.code === 'premium_required' || err?.status === 402) nextState = 'premium';
      else if (err?.code === 'clip_unavailable' || err?.code === 'memory_not_found' || err?.status === 404) nextState = 'unavailable';
      else nextState = 'error';
    }
    if (request !== requestRef.current) return clipRef.current;
    clipRef.current = next;
    setClip(next);
    setState(nextState);
    return next;
  }, [memoryId]);

  const fresh = useCallback(async () => {
    const current = clipRef.current;
    const expires = current?.expiresAt ? Date.parse(current.expiresAt) : NaN;
    if (current && (Number.isNaN(expires) || expires > nowRef.current())) return current;
    return load();
  }, [load]);

  useEffect(() => {
    if (state !== 'processing') return undefined;
    const timer = setTimeout(load, pollMs);
    return () => clearTimeout(timer);
  }, [state, load, pollMs]);

  // A different memory starts over; an unmounted one drops late answers.
  useEffect(() => {
    clipRef.current = null;
    setClip(null);
    setState('idle');
    return () => {
      requestRef.current += 1;
    };
  }, [memoryId]);

  return { state, clip, load, fresh, refresh: load };
}
